import { attach, Effect, forward, guard, merge, sample } from 'effector';

import { navigate } from 'libs/router';
import { log } from 'model/helpers/log';
import { RootDomain } from 'model/root.model';
import { routes } from 'features/verify/routes';
import { sessionConfig$ } from 'model/session.model';
import { ApiException, http } from 'model/helpers/http';
import { Photo, PhotoStep, PhotoType } from 'model/domain';
import { RequestException } from 'libs/request';

type UploadedPhotoMeta = {
	document_title: string;
	document_id: string;
	document_type: number;
	icao: string;
	country_alpha2: string;
	session_id: string;

}
type UploadFileResult = {
	id: string;
	meta: UploadedPhotoMeta;
	next_required: boolean;
}

export const MAX_ATTEMPTS = 3;
export const setStep = RootDomain.event<PhotoStep>();
export const verificationCancel = RootDomain.event();
export const openVerificationExitPopup = RootDomain.event();
export const closeVerificationExitPopup = RootDomain.event();

export const step$ = RootDomain.store(PhotoStep.front_side);
export const uploadAttempts$ = RootDomain.store(0);
export const uploadedPhotoMeta$ = RootDomain.store<UploadedPhotoMeta>(null);
export const photoList$ = RootDomain.store<string[]>([]);
export const isVerificationExit$ = RootDomain.store(false);
export const photo$ = RootDomain.store<Photo | null>(null);
export const errors$ = RootDomain.store<string[] | null>(null);
export const isUploading$ = RootDomain.store(false);

step$.watch(step => log({ name: 'change_step', message: { step } }));
uploadAttempts$.watch(attempts => log({ name: 'upload_attempts', message: { attempts } }));

export const photoType$ = step$.map(step => step === PhotoStep.selfie ? PhotoType.face : PhotoType.identity_document);
export const finishVerificationFx = attach({
	effect: http.post,
	source: {
		files: photoList$,
		sessionConfig: sessionConfig$,
	},
	mapParams(_: void, source) {
		const url = `/sessions/${source.sessionConfig.id}/complete`;
		const body = { files: source.files };

		return { url, body };
	},
});
export const uploadPhotoFx: Effect<{ photo: Photo, caution?: 'false' | 'true' }, UploadFileResult, RequestException<ApiException>> = attach({
	effect: http.post,
	source: {
		photo: photo$,
		type: photoType$,
		sessionConfig: sessionConfig$,
		attempts: uploadAttempts$,
	},
	mapParams(params: { photo: Photo, caution?: 'false' | 'true' }, source) {
		const url = `/sessions/${source.sessionConfig.id}/files`;
		const body = new FormData();
		body.append('file', params.photo);
		body.append('type', source.type);

		if (params.caution === 'true') {
			body.append('caution', params.caution);
		}

		return { url, body };
	},
});
export const onVerificationFinish = merge([finishVerificationFx.done, finishVerificationFx.fail]);

isUploading$
	.on(uploadPhotoFx, () => true)
	.on(uploadPhotoFx.finally, () => false);
uploadAttempts$
	.on(uploadPhotoFx.fail, (attempts) => attempts + 1)
	.on(uploadPhotoFx.done, () => 0);
isVerificationExit$
	.on(openVerificationExitPopup, () => true)
	.on(closeVerificationExitPopup, () => false);
photo$
	.on(uploadPhotoFx, (_, { photo }) => photo);
step$
	.on(setStep, (_, step) => step);
photoList$
	.on(uploadPhotoFx.doneData, (store, result: UploadFileResult) => [...store, result.id]);
errors$
	.on(uploadPhotoFx.fail, (_, { error }) => {
		if (!error.response) {
			throw error;
		}
		const { error_code } = error.response;

		return Array.isArray(error_code) ?
			error_code :
			[error_code];
	});

guard({
	source: uploadPhotoFx.doneData.map(data => data.meta),
	filter: step$.map(step => step === PhotoStep.front_side),
	target: uploadedPhotoMeta$,
});
guard({
	source: uploadPhotoFx.failData.map((payload: RequestException<ApiException<{ meta?: UploadedPhotoMeta }>>) => payload.response.meta ? payload.response.meta : null),
	filter: step$.map(step => step === PhotoStep.front_side),
	target: uploadedPhotoMeta$,
});

forward({
	from: sample({
		source: { step: step$ },
		clock: uploadPhotoFx.doneData,
		fn: ({ step }, response: UploadFileResult) => {
			if (step === PhotoStep.selfie) {
				return {
					step: null,
					route: routes.complete,
				};
			}
			if (step === PhotoStep.back_side) {
				return {
					step: PhotoStep.selfie,
					route: routes.selfie,
				};
			}
			if (step === PhotoStep.front_side && response.next_required) {
				return {
					step: PhotoStep.back_side,
					route: routes.document,
				};
			}

			return {
				step: PhotoStep.selfie,
				route: routes.selfie,
			};
		},
	}),
	to: [
		navigate.prepend(({ route }) => route),
		setStep.prepend(({ step }) => step),
	],
});
forward({
	from: uploadPhotoFx.fail,
	to: navigate.prepend(() => routes.photoError),
});
